import React, { useState } from "react";
import styled from "styled-components";

import Modal from "../modal/Modal";
// @ts-expect-error - JSX ファイルの型定義なし
import LanguageSwitcher from "../tab/LanguageSwitcher";
import Input from "../form-item/Input";
import Textarea from "../form-item/Textarea";

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// 補足フィールドが Textarea の label / option API を使うように移行したため、
// 旧 `FieldLabel` / `LabelText` styled-components は不要になった。

const ErrorBox = styled.div`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--soremo-green);
  border-radius: 4px;
  color: var(--soremo-green);
  white-space: pre-wrap;
  word-break: break-word;
`;

export type SectionKind = "section" | "subsection";

export interface SectionFormResult {
  title: string;
  title_en: string;
  note?: string;
  note_en?: string;
}

export interface SectionFormModalProps {
  /** 種別（セクション or サブセクション） */
  kind: SectionKind;
  /** 新規追加 / 既存編集 */
  mode?: "create" | "edit";
  initialValues?: Partial<SectionFormResult>;
  onSubmit: (result: SectionFormResult) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  submitError?: string | null;
}

const PLACEHOLDERS: Record<SectionKind, { jp: string; en: string }> = {
  section: { jp: "スタッフ", en: "Staff" },
  subsection: { jp: "演出", en: "Direction" },
};

const SectionFormModal: React.FC<SectionFormModalProps> = ({
  kind,
  mode = "create",
  initialValues,
  onSubmit,
  onClose,
  isSubmitting = false,
  submitError = null,
}) => {
  const [language, setLanguage] = useState<"JP" | "EN">("JP");
  const [title, setTitle] = useState(initialValues?.title ?? "");
  const [titleEn, setTitleEn] = useState(initialValues?.title_en ?? "");
  const [note, setNote] = useState(initialValues?.note ?? "");
  const [noteEn, setNoteEn] = useState(initialValues?.note_en ?? "");

  const isValid = title.trim().length > 0 || titleEn.trim().length > 0;

  const handleSubmit = () => {
    if (!isValid || isSubmitting) return;
    onSubmit(
      kind === "section"
        ? { title, title_en: titleEn, note, note_en: noteEn }
        : { title, title_en: titleEn },
    );
  };

  const isEdit = mode === "edit";
  const baseLabel = kind === "section" ? "セクション" : "サブセクション";
  const heading = isEdit ? `${baseLabel}を編集` : `${baseLabel}を追加`;
  const submitLabel = isEdit
    ? isSubmitting ? "保存中..." : "保存"
    : isSubmitting ? "追加中..." : "OK";
  const placeholder =
    PLACEHOLDERS[kind][language === "JP" ? "jp" : "en"];

  return (
    <Modal
      onClose={onClose}
      title={heading}
      primaryAction={{
        label: submitLabel,
        onClick: handleSubmit,
        disabled: !isValid || isSubmitting,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
        disabled: isSubmitting,
      }}
    >
      <FormContainer>
        {submitError && (
          <ErrorBox className="bodytext-13">{submitError}</ErrorBox>
        )}
        <Input
          label={language === "JP" ? "タイトル" : "Title"}
          option="required"
          type="text"
          placeholder={placeholder}
          width="100%"
          name={language === "JP" ? "title" : "title_en"}
          value={language === "JP" ? title : titleEn}
          onChange={(e) =>
            language === "JP"
              ? setTitle(e.target.value)
              : setTitleEn(e.target.value)
          }
        />
        {kind === "section" && (
          <Textarea
            label={language === "JP" ? "補足" : "Note"}
            option="optional"
            rows={3}
            placeholder={language === "JP" ? "本作の制作チーム" : ""}
            name={language === "JP" ? "note" : "note_en"}
            value={language === "JP" ? note : noteEn}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              language === "JP"
                ? setNote(e.target.value)
                : setNoteEn(e.target.value)
            }
          />
        )}
        <LanguageSwitcher onLanguageChange={setLanguage} />
      </FormContainer>
    </Modal>
  );
};

export default SectionFormModal;
